import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { verifyPhoneSchema } from "../../utils/error_schema";
import { resendPhoneOtpRequest, verifyPhoneOtpRequest } from "../../services";
import { FloatingField, SubmitButton } from "../../components/Common";
import { maskProtectedNumber, resolveApiFormErrors } from "../../utils";
import { useFormValidation, useTimerCount } from "../../utils/custom_hook";
import { storeAuthTokens, extractAuthData } from "../../utils/authHelpers";
import { toast } from "react-toastify";

const OTP_RESEND_MESSAGE_TIMEOUT = 5000; // 5 seconds
const INITIAL_FORM_VALUES = { mobile_code: "" };
const INITIAL_FORM_ERRORS = { mobile_code: "" };
const API_FIELD_MAP = { otp: "mobile_code", mobile_code: "mobile_code" };

export function VerifyPhone() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { timer, resetTimer } = useTimerCount();
  const paramData = Object.fromEntries([...searchParams]);

  const [isLoading, setIsLoading] = useState(false);
  const [resMessage, setResMessage] = useState({ type: "", message: "" });
  const timeoutRef = useRef(null);

  const {
    inputValue,
    inputError,
    setInputError,
    handleInputChange,
    validateForm,
  } = useFormValidation(
    INITIAL_FORM_VALUES,
    INITIAL_FORM_ERRORS,
    verifyPhoneSchema
  );

  const submit = async (e) => {
    e.preventDefault();
    const isValid = await validateForm();

    if (!isValid) return;

    setIsLoading(true);
    setResMessage({ type: "", message: "" });
    setInputError(INITIAL_FORM_ERRORS);

    try {
      const res = await verifyPhoneOtpRequest({
        otp: inputValue?.mobile_code,
        user_id: params.id,
      });

      if (res?.success) {
        const authData = extractAuthData(res);

        if (authData) {
          storeAuthTokens(authData.access, authData.refresh, authData.user);
          navigate("/home");
          return;
        }

        // Verification successful but no tokens (user needs to login)
        toast.success("Verification Successful, Please login to continue!");
        navigate("/login");
      } else {
        const { fieldErrors, displayError } = resolveApiFormErrors(
          res,
          INITIAL_FORM_ERRORS,
          { fieldMap: API_FIELD_MAP, fallbackMessage: "Verification failed." }
        );
        setInputError(fieldErrors);
        setResMessage({ type: "error", message: displayError });
      }
    } catch (error) {
      setResMessage({ type: "error", message: "Something went wrong! Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    try {
      const res = await resendPhoneOtpRequest({ user_id: params.id });

      if (res?.success) {
        setResMessage({ type: "success", message: res?.results?.message });
        timeoutRef.current = setTimeout(() => {
          setResMessage({ type: "", message: "" });
        }, OTP_RESEND_MESSAGE_TIMEOUT);
      } else {
        const { displayError } = resolveApiFormErrors(res, INITIAL_FORM_ERRORS, {
          fieldMap: API_FIELD_MAP,
          fallbackMessage: "Failed to resend OTP.",
        });
        setResMessage({ type: "error", message: displayError });
      }
      resetTimer(1);
    } catch (error) {
      setResMessage({ type: "error", message: "Failed to resend OTP. Please try again." });
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const getMaskedMessage = () => {
    if (paramData.otp_source === 'email') {
      return `Enter the 4-digit OTP sent to Email\n${paramData?.email}`;
    }
    return `Enter the 4-digit OTP sent to Mobile\n${maskProtectedNumber(paramData?.phone_number)}`;
  };
  return (
    <AuthLayout title="Login">
      <form onSubmit={submit}>
        <div className="text-2xl mb-8">
          {getMaskedMessage()}
        </div>

        <div className="mb-4">
          <FloatingField
            controlId="floatingInput"
            label="OTP"
            labelClass="_inInput_fx"
            placeholder="OTP"
            name="mobile_code"
            maxLength={4}
            onChange={({ target: { name, value } }) => handleInputChange(name, value)}
            focus={!!inputError.mobile_code}
            error={inputError.mobile_code}
            value={inputValue.mobile_code}
          />

          {timer ? (
            <p className="inactive_color mt-2">
              Resend OTP in &nbsp;
              <span className="active_color">{timer} seconds</span>
            </p>
          ) : (
            <p className="active_color pointer mt-2" onClick={resendOtp}>
              Resend OTP
            </p>
          )}
        </div>

        {resMessage.message && (
          <p className={`mt-2 ${resMessage.type === "success" ? "text-success" : "invalid-feedback"}`}>
            {resMessage.message}
          </p>
        )}

        <div className="mt-4">
          <SubmitButton text="Verify OTP" isLoading={isLoading} className="button sub_primary" />
        </div>
      </form>
    </AuthLayout>
  );
}
